import { useState, useEffect } from "react";
import api from "@/lib/api";
import { imgProxy } from "@/lib/img";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, ExternalLink, AlertTriangle, CheckCircle2, GitCompareArrows,
} from "lucide-react";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85";

export default function CompareDialog({ ids, open, onOpenChange, onOpenProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onlyDiff, setOnlyDiff] = useState(false);

  useEffect(() => {
    if (!open || !ids || ids.length < 2) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      setData(null);
      try {
        const res = await api.post("/profiles/compare", { ids });
        if (active) setData(res.data);
      } catch (e) {
        if (active) setError(e.response?.data?.detail || "Não foi possível comparar.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, ids]);

  const profiles = data?.profiles || [];
  const rows = (data?.rows || []).filter((r) => (onlyDiff ? !r.consistent : true));
  const cols = profiles.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="compare-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-head font-normal text-lg flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-[#c8aa6e]" />
            Comparar perfis
            {data && (
              <span className={`text-xs font-sans px-2 py-0.5 ml-1 ${data.inconsistencies > 0 ? "text-[#f59e0b] border border-[#f59e0b]/40" : "text-[#22c55e] border border-[#22c55e]/40"}`}>
                {data.inconsistencies > 0
                  ? `${data.inconsistencies} inconsistência(s)`
                  : "Dados consistentes"}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#71717a] py-16 justify-center">
            <Loader2 className="w-4 h-4 spin" /> A comparar perfis…
          </div>
        )}
        {!loading && error && (
          <div className="text-sm text-[#f87171] py-8 text-center">{error}</div>
        )}

        {!loading && data && (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-[#a1a1aa] flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-[#f59e0b]" />
                Linhas a amarelo = o mesmo campo tem valores diferentes entre perfis.
              </p>
              <label className="text-xs text-[#a1a1aa] flex items-center gap-1.5 cursor-pointer select-none">
                <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} data-testid="compare-onlydiff" />
                Só divergências
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="compare-table">
                <thead>
                  <tr>
                    <th className="text-left p-2 align-bottom label-tiny w-40">Campo</th>
                    {profiles.map((p) => (
                      <th key={p.id} className="p-2 align-bottom text-center" style={{ width: `${60 / cols}%` }}>
                        <button
                          type="button"
                          onClick={() => onOpenProfile && onOpenProfile(p.id)}
                          className="flex flex-col items-center gap-1 mx-auto group"
                          title="Abrir perfil"
                        >
                          <img
                            src={imgProxy((p.photos && p.photos[0]) || p.photo_url, 160) || PLACEHOLDER}
                            onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="w-14 h-14 object-cover border border-[#27272a] group-hover:border-[#c8aa6e] bg-[#27272a]"
                          />
                          <span className="text-[#fafafa] text-xs font-medium leading-tight group-hover:text-[#c8aa6e]">{p.name || "—"}</span>
                          <span className="text-[10px] text-[#71717a]">{p.source_name}</span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.key}
                      data-testid={`compare-row-${r.key}`}
                      className={`border-t border-[#27272a] ${!r.consistent ? "bg-[#f59e0b]/10" : ""}`}
                    >
                      <td className="p-2 text-[#a1a1aa] align-top">
                        <span className="inline-flex items-center gap-1.5">
                          {!r.consistent
                            ? <AlertTriangle className="w-3.5 h-3.5 text-[#f59e0b] shrink-0" />
                            : <CheckCircle2 className="w-3.5 h-3.5 text-[#3f3f46] shrink-0" />}
                          {r.label}
                        </span>
                      </td>
                      {r.values.map((v, i) => (
                        <td key={i} className={`p-2 text-center align-top ${!r.consistent && v ? "text-[#fcd34d] font-medium" : "text-[#e4e4e7]"}`}>
                          {v || <span className="text-[#52525b]">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={cols + 1} className="p-8 text-center text-[#71717a] text-sm">Sem campos para mostrar.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-4 border-t border-[#27272a] pt-3 mt-2 text-xs">
              {profiles.map((p) => (
                p.profile_url && (
                  <a key={p.id} href={p.profile_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#c8aa6e] hover:text-[#d4b982]">
                    <ExternalLink className="w-3 h-3" /> {p.name}
                  </a>
                )
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
